import { useQuery } from '@apollo/client/react'

import { graphql } from '../../../gql/gql'
import type { GetPlanSelectorDataQuery } from '../../../gql/graphql'
import { useOrganizationId } from '../../../hooks/useCurrentOrganization'

export const GetPlanSelectorData = graphql(/* GraphQL */ `
  query GetPlanSelectorData($input: PlanSelectorDataInput!) {
    planSelectorData(input: $input) {
      ... on PlanSelectorData {
        __typename
        organizationId
        requiresAssistedBilling
        hasEverBeenPayingCustomer
        hasDefaultStripePaymentMethod
        nextInvoice {
          totalAmount
          currency
          taxAmount
        }
        balance {
          amount
          currency
        }
        subscription {
          interval
          plan {
            id
            name
            interval
            limits {
              channels
              members
              scheduledPosts
            }
            pricing {
              unitPrice
              currency
              upperBound
              flatFee
            }
          }
          quantity
          isCanceledAtPeriodEnd
          periodEnd
          canceledAt
          trial {
            startDate
            endDate
          }
          isPaymentPastDue
          scheduledUpdate {
            newPlan
            newQuantity
            newInterval
            effectiveDate
          }
        }
        discounts {
          ... on AmountDiscount {
            amountOff: value
            currency
            scope
            duration
            durationInMonths
          }
          ... on PercentDiscount {
            percentOff: value
            scope
            duration
            durationInMonths
          }
        }
        availablePlans {
          planId
          planName
          planInterval
          pricing {
            unitPrice
            currency
            upperBound
            flatFee
          }
          pricingMetadata {
            quantityType
          }
        }
        gateway
      }
    }
  }
`)

export const usePlanSelectorQuery = (): {
  data: NonNullable<GetPlanSelectorDataQuery['planSelectorData']> | null
  loading: boolean
  error: Error | null
  refetch: () => Promise<unknown>
} => {
  const organizationId: string | null = useOrganizationId()
  const { data, loading, error, refetch } = useQuery<GetPlanSelectorDataQuery>(
    GetPlanSelectorData,
    {
      variables: {
        input: {
          organizationId: organizationId ?? '',
        },
      },
      skip: !organizationId,
    },
  )

  if (!data?.planSelectorData) {
    return {
      data: null,
      loading,
      error: error ? new Error(error.message) : null,
      refetch,
    }
  }

  // Handle PlanSelectorData success case
  if (data.planSelectorData.__typename === 'PlanSelectorData') {
    return {
      data: data.planSelectorData,
      loading,
      error: error ? new Error(error.message) : null,
      refetch,
    }
  }

  // Fallback for unexpected cases
  return {
    data: null,
    loading,
    error: new Error('Unknown error: Unable to fetch plan selector data'),
    refetch,
  }
}
